<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div
            class="text-center my-5"
        >
            <transition mode="out-in" enter-active-class="animate__animated animate__fadeIn" leave-active-class="animate__animated animate__fadeOutDown">
            
                <div v-if="mainState === 'checking'" key="checking" class="w-100 h-100">
                    <busy-spinner text="Checking setup information..."></busy-spinner>
                    <!-- <h4 class="d-inline-block mr-3">Checking all setup information...</h4> -->
                </div>

                <div v-if="mainState === 'draftReady'" key="draftReady" class="w-100 h-100">
                    <h6 class="text-muted mb-4">
                        <b-icon icon="check-circle-fill" variant="success" class="mr-2"></b-icon>
                        All checks pass
                    </h6>
                    <h4>The {{eventTypeName}} is ready to launch.</h4> 
                    <p class="mb-5">
                        <span v-if="rfxData.response_counts.queued">
                            There are {{ rfxData.response_counts.queued }} queued invitations ready to be sent.
                        </span>
                        <span v-else>
                            Once you launch the {{eventTypeName}} we'll direct you to invite suppliers.
                        </span>
                    </p> 
                    <b-button 
                        ref="launchButton"
                        size="lg" 
                        variant="success" 
                        :disabled="loadingLaunch"
                        class="py-3 px-4 rounded-pill font-size-110 min-width-200" 
                        @click="launchEvent"
                    >
                        <b-spinner v-if="loadingLaunch" key="loadingLaunchSpinner" small class="mr-2"></b-spinner>
                        <b-icon v-else key="notLoadingLaunch" icon="play-fill" class="mr-2"></b-icon>
                        {{ loadingLaunch ? 'Launching...' : `Launch ${eventTypeName}` }}
                    </b-button>
                </div>

                <div v-if="mainState === 'draftErrors'" key="draftErrors" class="w-100 h-100 animate__animated animate__headShake">
                    <h5 class="text-danger">The following things must be fixed:</h5> 
                    <div class="d-flex justify-content-center mt-3 text-left">
                        <div class="max-width-400 w-100">
                            <div v-for="[group, errors] in Object.entries(goLiveErrors)" :key="group" class="p-3 bg-danger-100 border-danger border">
                                <h6 class="mb-2 mr-3">{{errorGroupNames[group]}}</h6>
                                <ul class="list-flush">
                                    <li v-for="error in errors" :key="error">{{error}}</li>
                                </ul>
                                <b-link @click="requestActivateTab(group)" class="mt-3">Fix Now &rarr;</b-link>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="mainState === 'newOpen'" key="newOpen" class="w-100 h-100">
                    <h4 class="mb-4">
                        <b-icon icon="check-circle-fill" variant="success" class="mr-2"></b-icon>
                        The {{eventTypeName}} is now open.
                    </h4>
                    <div class="d-flex justify-content-center mt-3 text-left">
                        <div class="max-width-400 w-100">
                            <ul class="list-flush">
                                <li class="mb-3">You can come back to this Setup section anytime if you need to change any settings.</li>
                                <li class="mb-3">If you make any changes that affect already-submitted responses, it is best to notify suppliers of the changes using a Bulk Message sent from the <b-link :href="rfxData.internal_url">Event Dashboard</b-link></li>
                            </ul>
                        </div>
                    </div>
                    <h5 class="d-inline-block mr-3 mt-4">Next:</h5>
                    <b-button variant="white-primary" size="lg" :href="rfxData.manage_invites_url">
                        Invite suppliers to respond
                        <b-icon icon="arrow-right" class="ml-2"></b-icon>
                    </b-button>
                </div>

                <div 
                    v-if="mainState === 'open' || mainState === 'other'" 
                    key="open" 
                    class="w-100 h-100"
                >

                    <h4 v-if="mainState === 'open'">
                        The {{eventTypeName}} is currently 
                        <status-object-badge
                            :status-object="rfxData.state_dict"
                            text-uppercase
                            hide-icon
                            font-size="90%"
                        ></status-object-badge>
                    </h4> 
                    <div v-if="mainState === 'other'">
                        <h4>The {{eventTypeName}} is currently in "{{rfxData.state_dict ? rfxData.state_dict.title : 'some other'}}" mode.</h4>
                        <p>To change the status, go to the Event Dashboard <b-link :href="rfxData.internal_url">here</b-link></p> 
                    </div>
                    
                    <div class="d-flex justify-content-center mt-3">
                        <div class="max-width-500 w-100">
                            <h5 class="text-left">Go to:</h5>
                            <b-list-group class="shadow-sm">
                                <b-list-group-item 
                                    class="d-flex justify-content-between align-items-center"
                                    :href="rfxData.internal_url"
                                >
                                    <span class="h5 mb-0">Event Dashboard</span>
                                    <div>
                                        <b-icon icon="chevron-right" class="ml-3" variant="primary"></b-icon>
                                    </div>
                                    <!-- <b-button >
                                        Dashboard
                                        <b-icon icon="arrow-right" class="ml-2"></b-icon>
                                    </b-button> -->
                                </b-list-group-item>
                                <b-list-group-item 
                                    class="d-flex justify-content-between align-items-center"
                                    :href="rfxData.manage_invites_url"
                                >
                                    
                                        <span class="h5 mb-0 mr-3">Invite Suppliers</span>
                                        <div>
                                            <span>{{rfxData.invited_count ? rfxData.invited_count.toString() + ' invitations' : 'No invitations yet' }}</span>
                                            <b-icon icon="chevron-right" class="ml-3" variant="primary"></b-icon>
                                        </div>
                                        
                                    
                                    <!-- <b-button >
                                        Invitations
                                        <b-icon icon="arrow-right" class="ml-2"></b-icon>
                                    </b-button> -->
                                </b-list-group-item>
                                <b-list-group-item 
                                    class="d-flex justify-content-between align-items-center"
                                    :href="rfxData.internal_manage_url"
                                >
                                    
                                        <span class="h5 mb-0 mr-3">Manage Suppliers</span>
                                        <div>
                                            <span>{{rfxData.submitted_count ? rfxData.submitted_count.toString() + ' responses' : 'No responses yet' }}</span>
                                            <b-icon icon="chevron-right" class="ml-3" variant="primary"></b-icon>
                                        </div>
                                        
                                    
                                    <!-- <b-button >
                                        Responses
                                        <b-icon icon="arrow-right" class="ml-2"></b-icon>
                                    </b-button> -->
                                </b-list-group-item>
                            </b-list-group>
                        </div>
                    </div>
                </div>
            
            </transition>
        </div>
    
    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import StatusObjectBadge from './functional-comps/StatusObjectBadge';
import { Rfx } from '../js/models/rfx';
import { fireConfettiFromElement } from '../js/confetti';

export default {
    name: 'RfxSetupLaunch',
    components: {
        StatusObjectBadge,
    },
    props: {
        rfxId: {
            type: [String, Number],
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingChecks: false,
            loadingLaunch: false,
            originalState: '',  // set the ORIGINAL sm_state when we instantiated... so we know if we are setting to open for first time, or if it was already open
            setupValid: false,  // set this after calling API to see if its ready
            goLiveErrors: {},  // dict with errors
            rfxData: {},  // stash all rfx data here after getting from API
            errorGroupNames: {  // maps group names returned from API to name that you use on front-end (text) and the tab index number
                // 'settings' 'content' 'forms' 'criteria'
                settings: 'Settings',
                content: 'Content',
                forms: 'Response Format',
                criteria: 'Decision Criteria',
            }
        }
    },
    computed: {
        statusIsDraft: function() {
            // Boolean for sm_state === 'draft'
            if (this.rfxData && this.rfxData.sm_state) return this.rfxData.sm_state === 'draft';  // this will return a boolean
            return null;  // return null otherwise, like when we don't have API data yet
        },
        statusIsOpen: function() {
            // Boolean for sm_state === 'open'
            if (this.rfxData && this.rfxData.sm_state) return this.rfxData.sm_state === 'open';  // this will return a boolean
            return null;  // return null otherwise, like when we don't have API data yet
        },
        statusIsOther: function() {
            // Boolean for sm_state is something other than draft or open... show redirect to event dashboard instead?
            const opts = ['draft', 'open'];
            if (this.rfxData && this.rfxData.sm_state) return !opts.includes(this.rfxData.sm_state);  // this will return a boolean
            return null;  // return null otherwise, like when we don't have API data yet
        },
        readyToLaunch: function() {
            // combines setupValid (api checks pass on setup data) and statusIsDraft
            return this.setupValid && this.statusIsDraft;
        },
        mainState: function() {
            // main state for this component -- 'checking' 'draftReady' 'draftErrors' 'newOpen' 'open' 'other'
            if (this.loadingChecks) return 'checking';
            if (this.originalState === 'draft' && this.statusIsOpen) return 'newOpen';
            if (this.statusIsDraft && this.setupValid) return 'draftReady';
            if (this.statusIsDraft && !this.setupValid) return 'draftErrors';
            if (this.statusIsOpen) return 'open';
            return 'other'; // fallback 
        },
        eventTypeName() {
            return this.rfxData?.rfx_type ?? 'event';
        },
    },
    created: function() {
        this.loadingChecks = true;
    },
    mounted: function() {
        this.checkGoLive();
    },
    methods: {
        okToContinue: function(tabChangeEvent) {
            // PUBLIC METHOD CALLED FROM PARENT - takes tabChangeEvent as arg, return Promise that resolves to boolean where true = can continue
            console.log('from component okToContinue, tabChangeEvent is below:');
            console.log(tabChangeEvent);
            return Promise.resolve(true);  // change later
        },
        // PUBLIC METHODS ABOVE, private methods below
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        checkGoLive: function() {
            this.loadingChecks = true;
            return suppliosApi.rfx.checkGoLive(this.rfxId)
            .then(response => {
                console.log('response from checkGoLive');
                console.log(response);
                let respData = response.data;
                this.setupValid = respData.ready;  // this is a boolean of ready or not
                this.goLiveErrors = respData.errors;  // this is a dict with keys of 'settings' 'content' 'forms' 'criteria' and list of strings in each with any errors
                this.rfxData = new Rfx(respData.rfx_data);  // data from NewRfxDynoSerializer
                if (this.rfxData.sm_state && this.originalState === '') {
                    // if you have a sm_state, and originalState was not set yet (empty string) then set it here... but don't set it again (string won't be empty in that case)
                    this.originalState = this.rfxData.sm_state;
                } 
                if (!this.setupValid) this.reportTabError(this.goLiveErrors);
                return true;
            })
            .catch(error => {
                this.handleError(error, 'Error while checking if event can be launched');
                return false;
            })
            .then(launchSuccess => {
                this.loadingChecks = false;
                return launchSuccess;
            });
        },
        launchEvent: function() {
            this.loadingLaunch = true;
            return suppliosApi.rfx.launchEvent(this.rfxId)
            .then(response => {
                // Dec 4 2021 - now with confetti!
                const launchButton = this.$refs['launchButton'];
                if (launchButton) {
                    fireConfettiFromElement(launchButton);
                }
                console.log('lauched event via api, response below');
                console.log(response);
                this.rfxData = new Rfx(response.data); // it returns the updated rfx data, same format as other api call
                this.refreshRfxHeader();
                return true;
            })
            .catch(error => {
                this.handleError(error, 'Error while launching the event');
                return false;
            })
            .then(successStatus => {
                this.loadingLaunch = false;
                return successStatus;
            })
        },
        requestActivateTab: function(tabName) {
            // emit event to parent asking to activate a certain tab, using tab name from API
            this.$emit('go-to-tab', tabName);
        },
        reportTabError: function(errorObject) {
            // tell parent that we found a problem during error checking
            // emit name of tab(s) in array of strings, but parent will have to map those names to something
            this.$emit('tab-error', Object.keys(errorObject));  // emits array of tab names (API format) that have issues
        },
        refreshRfxHeader: function() {
            this.$root.$refs.rfxInternalHeader.refreshData();
        }
    }
}
</script>

<style>

</style>